/** @jsxImportSource @emotion/react */
import React, { memo, useEffect, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTheme } from '../provider/useTheme';
import type { ButtonTypeToken } from '../themes/types';
import type { IconName } from '../icons';
import { Text } from './text';
import { CoreIcon } from './CoreIcon';
import { CoreButton } from './CoreButton';
import { zIndex } from '../constants/zIndex';

export type CoreBannerType = 'error' | 'default' | 'info' | 'warning' | 'success';

export interface CoreBannerButton {
  label: string;
  onClick?: () => void;
  iconLeft?: IconName;
  iconRight?: IconName;
  type?: ButtonTypeToken;
}

export interface CoreBannerProps {
  visible: boolean;
  type?: CoreBannerType;
  icon?: IconName;
  titlePath: string;
  descriptionPath?: string;
  buttons?: CoreBannerButton[];
  value?: Record<string, string | number | boolean>;
}

const TRANSITION_DURATION_MS = 200;

/**
 * CoreBanner
 *
 * A bottom-fixed notification banner with semantic variants, optional icon,
 * translated title/description, and action buttons.
 */
export const CoreBanner: React.FC<CoreBannerProps> = memo(
  ({ visible, type = 'default', icon, titlePath, descriptionPath, buttons, value }) => {
    const theme = useTheme();
    const { getColor, getSpacing } = theme;
    // TODO(vitvio-a11y): Audit text/action contrast across banner variants and align button tokens to pass WCAG AA.

    const [isVisible, setIsVisible] = useState(visible);

    useEffect(() => {
      // Trigger CSS transition on visibility changes
      requestAnimationFrame(() => setIsVisible(visible));
    }, [visible]);

    const backgroundColorToken = useMemo(() => {
      switch (type) {
        case 'error':
          return 'bg.error.solid' as const;
        case 'warning':
          return 'bg.warning.solid' as const;
        case 'success':
          return 'bg.success.solid' as const;
        case 'info':
          return 'bg.info.solid' as const;
        case 'default':
        default:
          return 'bg.inverted' as const;
      }
    }, [type]);

    const padding = getSpacing('4');
    const gap = getSpacing('4');
    const verticalGap = getSpacing('2');

    const semanticForegroundToken = useMemo(
      () => (type === 'warning' || type === 'success' ? ('fg' as const) : ('fg.inverted' as const)),
      [type],
    );

    return (
      <div
        css={css`
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          justify-content: center;
          pointer-events: none;
          z-index: ${zIndex.banner};
        `}
      >
        <div
          css={css`
            pointer-events: auto;
            width: 100%;
            transform: translateY(${isVisible ? '0%' : '100%'});
            opacity: ${isVisible ? 1 : 0};
            transition:
              transform ${TRANSITION_DURATION_MS}ms ease-out,
              opacity ${TRANSITION_DURATION_MS}ms ease-out;
            background-color: ${getColor(backgroundColorToken)};
            padding: ${padding};
            display: flex;
            align-items: center;
            gap: ${gap};
          `}
        >
          {/* Left section - optional icon */}
          {icon && (
            <div
              css={css`
                flex: 0 0 auto;
                display: flex;
                align-self: flex-start;
                margin-top: ${getSpacing('0.5')};
              `}
            >
              <CoreIcon icon={icon} size="lg" color={semanticForegroundToken} />
            </div>
          )}

          {/* Middle section - title and description */}
          <div
            css={css`
              flex: 1 1 auto;
              display: flex;
              flex-direction: column;
              gap: ${verticalGap};
              color: ${getColor(semanticForegroundToken)};
            `}
          >
            <Text path={titlePath} font="headings.XS_SemiBold" colorToken={semanticForegroundToken} value={value} />
            {descriptionPath && (
              <Text path={descriptionPath} font="body.M_Regular" colorToken={semanticForegroundToken} value={value} />
            )}
          </div>

          {/* Right section - buttons */}
          {buttons && buttons.length > 0 && (
            <div
              css={css`
                flex: 0 0 auto;
                display: flex;
                align-items: center;
                gap: ${getSpacing('2')};
              `}
            >
              {buttons.map((button, index) => (
                <CoreButton
                  key={index}
                  size="sm"
                  label={button.label}
                  iconLeft={button.iconLeft}
                  iconRight={button.iconRight}
                  type={button.type || 'secondary'}
                  onClick={button.onClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
);

CoreBanner.displayName = 'CoreBanner';

export default CoreBanner;
